/*
Copyright (c) Uber Technologies, Inc.

This source code is licensed under the MIT license found in the
LICENSE file in the root directory of this source tree.
*/
import * as React from 'react';
import { getOverrides } from '../helpers/overrides';
import { Tag, HIERARCHY } from '../tag';
export default function MultiValue(props: any) {
  const {
    overrides = {},
    removeValue,
    ...restProps
  } = props;
  // todo(v11): remove the MultiValue override in favor of Tag
  const [MultiValue, tagProps] = getOverrides(overrides.Tag || overrides.MultiValue, Tag);
  // Only wire onClick when the tag is actually closeable. If either restProps,
  // tagProps, or overrides.MultiValue.props sets closeable=false (e.g.
  // disabled/readOnly Select overrides), the tag has no X and cannot be removed —
  // passing onClick would still flip Tag's internal `$clickable=true`, triggering
  // the interactive border/overlay styles even though the tag isn't interactive.
  // Check all three sources:
  //   restProps        — closeable passed as a direct prop
  //   tagProps         — closeable from overrides.Tag.props
  //   multiValueProps  — closeable from overrides.MultiValue.props. When a consumer
  //                      supplies both overrides.Tag (component) AND
  //                      overrides.MultiValue.props, getOverrides picks overrides.Tag
  //                      for the component, so overrides.MultiValue.props never
  //                      reaches tagProps — we must read it explicitly.
  const multiValueProps = (overrides.MultiValue as {
    props?: {
      closeable?: boolean;
    };
  })?.props ?? {};
  const isCloseable = restProps.closeable !== false && tagProps.closeable !== false && multiValueProps.closeable !== false;
  return <MultiValue hierarchy={HIERARCHY.primary} overrides={{
    Root: {
      // @ts-ignore
      style: ({
        $theme: {
          sizing
        }
      }) => ({
        marginRight: sizing.scale0,
        marginBottom: sizing.scale0,
        marginLeft: sizing.scale0,
        marginTop: sizing.scale0
      })
    }
  }} onActionClick={removeValue}
  // The Tag Root has role="button" / aria-label="Remove <value>" so it IS the
  // AT activation target. Wire onClick so that a VoiceOver double-tap or an
  // Enter/Space keypress on the focused tag calls removeValue — matching what
  // the visually-apparent "×" does. The Action's own onClick already calls
  // event.stopPropagation() before onActionClick, so there is no double-fire.
  // Guard on isCloseable: a non-closeable tag (disabled/readOnly) has no X and
  // must not gain $clickable styling just because removeValue is in scope.
  onClick={isCloseable ? removeValue : undefined} {...restProps} {...tagProps}>
      {props.children}
    </MultiValue>;
}